import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import {
  getAdminOverview,
  type AdminOverviewResponse,
} from "@/features/admin/adminApi";

const futureAdminAreas = [
  "Webhook replay",
  "Notifications",
  "GDPR requests",
];

function AccessDenied() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-semibold text-slate-500">Not found</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
        This page is not available
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
        The admin area is only available to authorised operators.
      </p>
    </section>
  );
}

export function AdminDashboard() {
  const { getToken, isLoaded } = useAuth();
  const [overview, setOverview] = useState<AdminOverviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDenied, setIsDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadOverview() {
      if (!isLoaded) {
        return;
      }

      setIsLoading(true);
      setIsDenied(false);
      setError(null);

      try {
        const token = await getToken();
        setOverview(await getAdminOverview(token));
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          setIsDenied(true);
          return;
        }

        setError(
          err instanceof Error
            ? err.message
            : "Admin overview is unavailable right now.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    void loadOverview();
  }, [getToken, isLoaded]);

  if (isLoading) {
    return <div className="h-72 animate-pulse rounded-lg bg-white" />;
  }

  if (isDenied) {
    return <AccessDenied />;
  }

  const metrics = [
    { label: "Users", value: overview?.overview.totalUsers ?? 0 },
    { label: "Projects", value: overview?.overview.totalProjects ?? 0 },
    { label: "Blueprints", value: overview?.overview.totalBlueprints ?? 0 },
    { label: "Tool results", value: overview?.overview.totalToolResults ?? 0 },
  ];

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">Admin</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
          Operational overview for Alchemy Blueprint Engine
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          Read-only foundation for future support, billing, entitlement, and
          operational workflows.
        </p>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
        <p className="text-sm font-semibold text-emerald-950">
          Admin access verified
        </p>
        <p className="mt-2 text-sm leading-6 text-emerald-900">
          {overview?.message ?? "Admin foundation is active."}
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
          >
            <p className="text-sm text-slate-500">{metric.label}</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">
              {metric.value}
            </p>
          </div>
        ))}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div>
          <p className="text-sm font-semibold text-indigo-700">Coming next</p>
          <h3 className="mt-2 text-lg font-semibold text-slate-950">
            Future admin areas
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            These features are planned for future phases.
          </p>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Link
            to="/admin/users"
            className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm font-semibold text-indigo-800 transition hover:bg-indigo-100"
          >
            Users — read-only
            <span className="mt-1 block font-normal text-indigo-700">
              View accounts and access state
            </span>
          </Link>
          <Link
            to="/admin/purchase-events"
            className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm font-semibold text-indigo-800 transition hover:bg-indigo-100"
          >
            Purchase Events — read-only
            <span className="mt-1 block font-normal text-indigo-700">
              Inspect payment, webhook and manual access logs
            </span>
          </Link>
          <Link
            to="/admin/promo-codes"
            className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm font-semibold text-indigo-800 transition hover:bg-indigo-100"
          >
            Access Codes
            <span className="mt-1 block font-normal text-indigo-700">
              Create and manage access codes for beta users, support and promotions
            </span>
          </Link>
          <Link
            to="/admin/metrics"
            className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm font-semibold text-indigo-800 transition hover:bg-indigo-100"
          >
            Metrics
            <span className="mt-1 block font-normal text-indigo-700">
              Usage, access activity and estimated AI cost
            </span>
          </Link>
          {futureAdminAreas.map((area) => (
            <div
              key={area}
              aria-disabled="true"
              className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm font-semibold text-slate-500"
            >
              {area}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
